/**
 * Alloy-native 2FA calls (ALL-5747, specs/alloy-mfa S-3..S-6, S-13).
 *
 * The `/v1/mfa/**` routes are the only product routes a session pending its
 * second factor may call. Every one is a POST: apiFetch adds the CSRF header,
 * and the gateway requires an Idempotency-Key on all but the two WebAuthn
 * options routes. The gateway relays the auth service's answer as is:
 * 401 MFA_CODE_INVALID (optionally `attemptsRemaining`), 429 MFA_LOCKED_OUT,
 * 403 AUTH_MFA_REQUIRED.
 *
 * TOTP secrets and recovery codes are returned to the caller only; nothing in
 * this module keeps them.
 */
import { apiFetch, idempotencyKey, responseSupport } from "@/lib/api";
import { MFA_REQUIRED_REASON } from "@/lib/terms-gate";

export type MfaSessionState =
  | "NOT_REQUIRED"
  | "ENROLMENT_SUGGESTED"
  | "ENROLMENT_REQUIRED"
  | "CHALLENGE_REQUIRED"
  | "VERIFIED";

const SAFE_PATH_ID = /^[A-Za-z0-9_-]{1,128}$/;

/** Why an MFA call did not pass. */
export type MfaFailureKind =
  /** 401 MFA_CODE_INVALID: wrong code or refused passkey; counted toward the lockout. */
  | "invalid"
  /** 429 MFA_LOCKED_OUT: too many failures; the session is revoked. */
  | "locked"
  /** 403 AUTH_MFA_REQUIRED: this step is not allowed in the session's current state. */
  | "required"
  /** Any other 401: the session itself is no longer valid. */
  | "unauthenticated"
  | "error";

export interface MfaFailure {
  ok: false;
  kind: MfaFailureKind;
  attemptsRemaining?: number;
  supportReference?: string;
}

export interface MfaChallengePassed {
  ok: true;
  mfaState: string;
  /** Non-empty only on the principal's first enrolment; shown once. */
  recoveryCodes: string[];
}

export interface TotpEnrolmentStarted {
  ok: true;
  enrolmentId: string;
  otpauthUri: string;
  secretBase32: string;
}

export type MfaResult<T> = T | MfaFailure;

function reasonOf(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const value = (body as Record<string, unknown>).reasonCode ?? (body as Record<string, unknown>).reason_code;
  return typeof value === "string" ? value : undefined;
}

function failure(res: Response | null, body: unknown): MfaFailure {
  if (!res) return { ok: false, kind: "error" };
  const reason = reasonOf(body);
  const support = responseSupport(res, body);
  if (res.status === 401 && reason === "MFA_CODE_INVALID") {
    const remaining = body && typeof body === "object" ? (body as Record<string, unknown>).attemptsRemaining : undefined;
    return {
      ok: false,
      kind: "invalid",
      ...(typeof remaining === "number" && Number.isFinite(remaining) && remaining >= 0
        ? { attemptsRemaining: Math.floor(remaining) }
        : {}),
      ...support,
    };
  }
  if (res.status === 429 && reason === "MFA_LOCKED_OUT") return { ok: false, kind: "locked", ...support };
  if (res.status === 403 && reason === MFA_REQUIRED_REASON) return { ok: false, kind: "required", ...support };
  if (res.status === 401) return { ok: false, kind: "unauthenticated", ...support };
  return { ok: false, kind: "error", ...support };
}

async function post(
  path: string,
  scope: string,
  options: { body?: string; idempotent: boolean },
): Promise<{ res: Response | null; body: unknown }> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.idempotent) headers["Idempotency-Key"] = idempotencyKey(scope);
  try {
    const res = await apiFetch(path, { method: "POST", headers, body: options.body }, scope);
    const text = await res.text().catch(() => "");
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
    }
    return { res, body };
  } catch {
    return { res: null, body: null };
  }
}

function challengeResult(res: Response | null, body: unknown): MfaResult<MfaChallengePassed> {
  if (!res || !res.ok || !body || typeof body !== "object") return failure(res, body);
  const value = body as Record<string, unknown>;
  const codes = Array.isArray(value.recoveryCodes)
    ? value.recoveryCodes.filter((code): code is string => typeof code === "string")
    : [];
  return { ok: true, mfaState: typeof value.mfaState === "string" ? value.mfaState : "", recoveryCodes: codes };
}

/** POST /v1/mfa/totp/enrolments: a new enrolment, its otpauth URI and secret (shown once). */
export async function startTotpEnrolment(): Promise<MfaResult<TotpEnrolmentStarted>> {
  const { res, body } = await post("/v1/mfa/totp/enrolments", "mfa_totp_enrol", { idempotent: true });
  if (!res || !res.ok || !body || typeof body !== "object") return failure(res, body);
  const value = body as Record<string, unknown>;
  if (
    typeof value.enrolmentId !== "string" ||
    !SAFE_PATH_ID.test(value.enrolmentId) ||
    typeof value.otpauthUri !== "string" ||
    !value.otpauthUri.startsWith("otpauth://") ||
    typeof value.secretBase32 !== "string"
  ) {
    return { ok: false, kind: "error", ...responseSupport(res, body) };
  }
  return { ok: true, enrolmentId: value.enrolmentId, otpauthUri: value.otpauthUri, secretBase32: value.secretBase32 };
}

/** POST /v1/mfa/totp/enrolments/{id}/confirm {code}. */
export async function confirmTotpEnrolment(enrolmentId: string, code: string): Promise<MfaResult<MfaChallengePassed>> {
  if (!SAFE_PATH_ID.test(enrolmentId)) return { ok: false, kind: "error" };
  const { res, body } = await post(`/v1/mfa/totp/enrolments/${enrolmentId}/confirm`, "mfa_totp_confirm", {
    idempotent: true,
    body: JSON.stringify({ code }),
  });
  return challengeResult(res, body);
}

/** POST /v1/mfa/challenges/totp {code}. */
export async function challengeTotp(code: string): Promise<MfaResult<MfaChallengePassed>> {
  const { res, body } = await post("/v1/mfa/challenges/totp", "mfa_totp_challenge", {
    idempotent: true,
    body: JSON.stringify({ code }),
  });
  return challengeResult(res, body);
}

/** POST /v1/mfa/challenges/recovery-code {code}. A passed recovery code leaves the session needing a new factor. */
export async function challengeRecoveryCode(code: string): Promise<MfaResult<MfaChallengePassed>> {
  const { res, body } = await post("/v1/mfa/challenges/recovery-code", "mfa_recovery_challenge", {
    idempotent: true,
    body: JSON.stringify({ code }),
  });
  return challengeResult(res, body);
}

async function webauthnOptions(path: string, scope: string): Promise<MfaResult<{ ok: true; options: unknown }>> {
  const { res, body } = await post(path, scope, { idempotent: false });
  if (!res || !res.ok || !body || typeof body !== "object") return failure(res, body);
  return { ok: true, options: body };
}

/** POST /v1/mfa/webauthn/registration-options: `{"publicKey": ...}` for navigator.credentials.create. */
export function passkeyRegistrationOptions() {
  return webauthnOptions("/v1/mfa/webauthn/registration-options", "mfa_passkey_options");
}

/** POST /v1/mfa/webauthn/registrations with the credential's standard JSON. */
export async function registerPasskey(credentialJson: string): Promise<MfaResult<MfaChallengePassed>> {
  const { res, body } = await post("/v1/mfa/webauthn/registrations", "mfa_passkey_register", {
    idempotent: true,
    body: credentialJson,
  });
  return challengeResult(res, body);
}

/** POST /v1/mfa/webauthn/assertion-options: `{"publicKey": ...}` for navigator.credentials.get. */
export function passkeyAssertionOptions() {
  return webauthnOptions("/v1/mfa/webauthn/assertion-options", "mfa_passkey_assert_options");
}

/** POST /v1/mfa/webauthn/assertions with the assertion's standard JSON. */
export async function verifyPasskeyAssertion(credentialJson: string): Promise<MfaResult<MfaChallengePassed>> {
  const { res, body } = await post("/v1/mfa/webauthn/assertions", "mfa_passkey_assert", {
    idempotent: true,
    body: credentialJson,
  });
  return challengeResult(res, body);
}

/** POST /v1/mfa/enrolments/skip: "Not now" at level enrol; the auth service audits MFA_ENROLMENT_SKIPPED. */
export async function skipMfaEnrolment(): Promise<MfaResult<{ ok: true }>> {
  const { res, body } = await post("/v1/mfa/enrolments/skip", "mfa_enrol_skip", { idempotent: true });
  if (!res || !res.ok) return failure(res, body);
  return { ok: true };
}
